import type { LaneDef, StatusDef } from '../../api/types';
import type { TransitionEdge } from './settings-api';

/**
 * The from→to matrix, as data.
 *
 * 🔴🔴 26 STATUSES IS 676 CELLS AND A GRID OF THEM IS NOT AN INTERFACE.
 *
 * A 26×26 table is 676 checkboxes, needs both axes labelled to be readable at all, does not fit on a
 * 1280px desktop let alone a 390px phone, and — the part that matters — answers a question nobody
 * asks. Nobody asks "show me the matrix". They ask **"from Code Review, where may this go?"**, which
 * is ONE ROW of it.
 *
 * ⇒ The editor shows one `from` status at a time with a checklist of permitted targets, grouped by
 * lane. That is the same shape on a phone and on a desktop, which is not a happy accident: a
 * question with one subject and a list of answers has no second axis to lose.
 *
 * ⭐ The lane grouping is doing real work rather than decorating: the 26 collapse to 6, and "from
 * Code Review you may reach anything in Development plus QA WIP" is a sentence a person can hold.
 */

export interface LaneGroup {
  lane: LaneDef;
  statuses: StatusDef[];
}

/**
 * Group the targets by lane, in the lanes' own order.
 *
 * ⚠️ A status whose `lane` matches no lane definition is NOT dropped — it lands in a trailing group
 * labelled with the raw lane code. Dropping it would remove a checkbox for an edge that exists in the
 * matrix, and the next save would silently delete that edge. ⛔ The one failure this whole editor
 * must not have is "the save removed something that was never on screen".
 */
export function groupByLane(statuses: readonly StatusDef[], lanes: readonly LaneDef[]): LaneGroup[] {
  const order = new Map(lanes.map((lane, index) => [lane.code, index]));
  const groups = new Map<string, LaneGroup>();

  for (const status of statuses) {
    const laneCode = status.lane;
    let group = groups.get(laneCode);
    if (!group) {
      const known = lanes.find((lane) => lane.code === laneCode);
      group = {
        lane: known ?? {
          code: laneCode,
          // ⭐ The raw code, not a title-cased guess. "we could not resolve this lane" must not look
          // like a lane called "Dev To Do".
          label: laneCode,
          sort_order: Number.MAX_SAFE_INTEGER,
          colour: null,
          is_active: true,
        },
        statuses: [],
      };
      groups.set(laneCode, group);
    }
    group.statuses.push(status);
  }

  return [...groups.values()].sort(
    (a, b) =>
      (order.get(a.lane.code) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(b.lane.code) ?? Number.MAX_SAFE_INTEGER) || a.lane.code.localeCompare(b.lane.code),
  );
}

/** Which statuses are reachable from `from`, in the matrix's own order. */
export function targetsFor(matrix: readonly TransitionEdge[], from: string): string[] {
  return matrix.filter((edge) => edge.from_status === from).map((edge) => edge.to_status);
}

/** The distinct `from` statuses that have at least one edge — used to mark dead ends in the picker. */
export function outboundCounts(matrix: readonly TransitionEdge[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const edge of matrix) {
    counts.set(edge.from_status, (counts.get(edge.from_status) ?? 0) + 1);
  }
  return counts;
}

/** How many statuses can reach each one — the other half of "is this status stranded". */
export function inboundCounts(matrix: readonly TransitionEdge[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const edge of matrix) {
    counts.set(edge.to_status, (counts.get(edge.to_status) ?? 0) + 1);
  }
  return counts;
}

/**
 * 🔴🔴 THE SAVE PAYLOAD. `PUT /settings/transitions` REPLACES THE WHOLE MATRIX.
 *
 * So this returns EVERY edge that should exist afterwards — the untouched rules for the other 25
 * statuses, plus exactly the checked targets for this one. ⛔ Sending only the edited status's edges
 * would delete the rest of the workflow, and the screen would look like it had saved.
 *
 * ⭐ NOTES SURVIVE. An edge that was already permitted keeps its `note` (db/006 puts guidance text
 * there, e.g. "failed during QA"); only genuinely new edges get `null`. Rebuilding every edge from
 * the checkbox state would wipe that text with no warning and no way to notice.
 *
 * ⚠️ Order is preserved for existing edges and new ones are appended in the order the target list
 * gave them, so a save that changes nothing produces a payload identical to what was read.
 */
export function applyTargets(
  matrix: readonly TransitionEdge[],
  from: string,
  targets: readonly string[],
): TransitionEdge[] {
  const wanted = new Set(targets);
  const kept: TransitionEdge[] = [];
  const seen = new Set<string>();

  for (const edge of matrix) {
    if (edge.from_status !== from) {
      kept.push(edge);
      continue;
    }
    if (wanted.has(edge.to_status) && !seen.has(edge.to_status)) {
      seen.add(edge.to_status);
      kept.push(edge);
    }
    // Everything else is an edge the user unchecked — dropped, which is the whole point.
  }

  for (const target of targets) {
    if (seen.has(target)) continue;
    seen.add(target);
    kept.push({ from_status: from, to_status: target, note: null });
  }

  return kept;
}

/** True when the checked set differs from what the matrix currently says. Drives the Save button. */
export function targetsChanged(
  matrix: readonly TransitionEdge[],
  from: string,
  targets: readonly string[],
): boolean {
  const current = new Set(targetsFor(matrix, from));
  if (current.size !== new Set(targets).size) return true;
  return targets.some((target) => !current.has(target));
}

/**
 * A one-line summary of the edited row — "12 of 26 permitted".
 * ⭐ The denominator is the number of ACTIVE statuses, because inactive ones are not offered as
 * targets; printing 26 when only 24 are selectable would make a complete row look incomplete.
 */
export function permittedSummary(targets: readonly string[], selectable: number): string {
  return `${targets.length} of ${selectable} permitted`;
}
